import { useRef, useState } from "react";
import { FaCloudUploadAlt, FaFileAlt, FaTimes } from "react-icons/fa";

/**
 * DropzoneCard - Drag-and-drop file picker card, styled to match the
 * Collaborators card below the sidebar. Collects files into local state
 * (drag/drop or click-to-browse); no backend upload endpoint exists yet,
 * so `onFilesAdded` is where a caller can wire persistence/upload later.
 * The Upload button (shown once at least one file is selected) calls
 * `onUpload(files)` — a caller passes this in to actually send the files.
 */
const DropzoneCard = ({
  onFilesAdded,
  onUpload,
  accept,
  multiple = true,
  title = "Attachments",
  hint = "Drag & drop files here, or click to browse",
}) => {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setFiles((prev) => {
      const next = multiple ? [...prev, ...incoming] : incoming.slice(0, 1);
      onFilesAdded?.(next);
      return next;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      onFilesAdded?.(next);
      return next;
    });
  };

  const handleUpload = async () => {
    if (files.length === 0 || isUploading) return;
    setIsUploading(true);
    try {
      // Callers are expected to handle/toast their own errors and resolve
      // normally either way - the queue always drains once the attempt is
      // done, so a failed upload doesn't need a second "clear" affordance.
      await onUpload?.(files);
      setFiles([]);
      onFilesAdded?.([]);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="dashboard-card p-3 mt-3">
      <h6 className="mb-2">{title}</h6>

      <div
        className={`dropzone${isDragOver ? " dropzone--active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
      >
        <FaCloudUploadAlt size={24} className="dropzone-icon" />
        <p className="dropzone-hint mb-0">{hint}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="d-none"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="dropzone-file-list list-unstyled mb-0 mt-2">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="dropzone-file-row">
              <FaFileAlt size={12} className="dropzone-file-icon" />
              <span className="dropzone-file-name">{file.name}</span>
              <button
                type="button"
                className="dropzone-file-remove"
                aria-label={`Remove ${file.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <FaTimes size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="dashboard-btn-primary w-100 mt-2"
        onClick={handleUpload}
        disabled={files.length === 0 || isUploading}
      >
        <FaCloudUploadAlt size={12} />
        <p className="mb-0">{isUploading ? "Uploading…" : "Upload"}</p>
      </button>
    </div>
  );
};

export default DropzoneCard;
